import React from 'react';

/**
 * Reusable empty-state card. One illustration slot, eyebrow, title,
 * supporting copy, and a single primary CTA. Designed to feel decisive
 * rather than apologetic — the goal is to push the user into the next
 * action immediately, not to offer five options.
 *
 * Props:
 *  - icon:       phosphor component (rendered at 48px, cyan)
 *  - eyebrow:    short uppercase label above the title (optional)
 *  - title:      h3-level headline
 *  - body:       1-2 sentence description (string or node)
 *  - cta:        { label: string, onClick: fn, testId?: string, icon?: phosphor }
 *  - secondary:  optional second CTA, same shape — ghost-styled
 *  - tone:       'light' (default, on white pages) | 'dark' (on Tools page)
 *  - testId:     wrapper data-testid
 */
export default function EmptyState({
    icon: Icon,
    eyebrow,
    title,
    body,
    cta,
    secondary,
    tone = 'light',
    testId,
}) {
    const isDark = tone === 'dark';
    const wrapperBase = 'flex flex-col items-center justify-center text-center px-6 py-12 border border-dashed';
    const wrapperTone = isDark
        ? 'border-white/25 text-white'
        : 'border-[#D4D4D8] bg-white text-[#09090B]';
    const eyebrowTone = isDark ? 'text-[#1FA5CF]' : 'text-[#1787AB]';
    const bodyTone = isDark ? 'text-white/70' : 'text-[#52525B]';
    const iconBg = isDark ? 'bg-white/10' : 'bg-[#E8F5FB]';
    const iconColor = isDark ? '#FFFFFF' : '#1787AB';

    return (
        <div className={`${wrapperBase} ${wrapperTone}`} data-testid={testId}>
            {Icon && (
                <div className={`w-16 h-16 mb-4 flex items-center justify-center ${iconBg}`}>
                    <Icon size={28} weight="bold" color={iconColor} />
                </div>
            )}
            {eyebrow && (
                <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-2 ${eyebrowTone}`}>
                    {eyebrow}
                </div>
            )}
            <h3 className="font-display font-bold text-xl leading-tight mb-2">{title}</h3>
            {body && (
                <p className={`text-sm max-w-sm leading-relaxed ${bodyTone}`}>{body}</p>
            )}
            {(cta || secondary) && (
                <div className="mt-5 flex flex-col sm:flex-row items-center gap-2">
                    {cta && (
                        <button
                            type="button"
                            onClick={cta.onClick}
                            data-testid={cta.testId}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] active:scale-95 transition-all duration-150 ${
                                isDark
                                    ? 'bg-white text-[#09090B] hover:bg-[#E8F5FB]'
                                    : 'bg-[#1FA5CF] text-white hover:bg-[#1787AB]'
                            }`}
                        >
                            {cta.icon && <cta.icon size={14} weight="bold" />}
                            {cta.label}
                        </button>
                    )}
                    {secondary && (
                        <button
                            type="button"
                            onClick={secondary.onClick}
                            data-testid={secondary.testId}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] active:scale-95 transition-all duration-150 ${
                                isDark
                                    ? 'text-white/80 hover:text-white hover:bg-white/10'
                                    : 'text-[#52525B] hover:text-[#09090B] hover:bg-[#F4F4F5]'
                            }`}
                        >
                            {secondary.icon && <secondary.icon size={14} weight="bold" />}
                            {secondary.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
